import React, { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import NButton from '../../components/inputs/NButton';
import useUserAPI, { useUserHook } from '../../hooks/network/useUserAPI';
import { RouteEnums } from '../../Routes/RoutesConstant';
import useAuthHook from '../../hooks/custom-hooks/useAuthHook';
import NSearch from '../../components/inputs/NSearch';
import NTable2 from '../../components/table/NTable2';
import SortableHeader from '../../components/table/SortableHeader';
import MobileEdit from '../../assets/svg/MobileEdit';
import MobileDelete from '../../assets/svg/MobileDelete';
import { successToaster } from '../../components/toaster/Toaster';
import NSearchableDropDown from '../../components/inputs/NSearchableDropdown';
import DownloadIcon from '@mui/icons-material/Download';
import useCustomerAPI from '../../hooks/network/useCustomerAPI';

function ManageUsers() {
  const navigate = useNavigate();
  const [customerOptions, setCustomerOptions] = useState();
  const { deleteUser } = useUserAPI();
  const { downloadUserList, isLoading } = useUserAPI();
  const { getCustomers } = useCustomerAPI();
  const { isUserHasEditAndDeletePermission } = useAuthHook();

  const {
    lastElementRef,
    users,
    isPaginationLoading,
    searchString,
    setSearchString,
    setCustomerId,
    customerId,
    projectIds,
    setProjectIds,
    fetchUsers,
    setPage,
    setUsers,
    sortDirection,
    setSortBy,
    setSortDirection,
  } = useUserHook();

  // local table sort key (must match column.key for header UI)
  const [orderByKey, setOrderByKey] = useState('');

  // map table column keys to backend sortBy values
  const sortKeyMap = {
    userName: 'FirstName',
    customers: 'CustomerName',
    projects: 'ProjectName',
    email: 'Email',
    loginEmail: 'LoginEmail',
  };

  const handleRequestSort = (property, order) => {
    const backendKey = sortKeyMap[property] || '';
    setOrderByKey(property);
    setSortBy(backendKey);
    setSortDirection(order);
    setUsers(undefined);
    setPage(1);
  };

  const handleHeaderClick = (property) => {
    const isAsc = orderByKey === property && sortDirection === 'ASC';
    const nextOrder = isAsc ? 'DESC' : 'ASC';
    handleRequestSort(property, nextOrder);
  };

  const columns = [
    {
      width: '1%',
      key: 'userName',
      isSortable: false,
      columClassName: 'break-all',
      headerRender: () => (
        <SortableHeader
          onClick={() => handleHeaderClick('userName')}
          active={orderByKey === 'userName'}
          order={sortDirection}
          className='break-all'
        >
          User <br /> Name
        </SortableHeader>
      ),
      rowRender: (rowData) => (
        <span>
          {rowData?.firstName} {rowData?.lastName}
        </span>
      ),
    },
    {
      width: '1%',
      key: 'userRole',
      title: 'User Type',
      isSortable: false,
      headerRender: () => (
        <>
          User <br /> Type
        </>
      ),
      rowRender: (rowData) => <span> {rowData?.userRole} </span>,
    },
    {
      width: '20%',
      key: 'customers',
      title: 'Customer',
      isSortable: false,
      headerRender: () => (
        <SortableHeader
          onClick={() => handleHeaderClick('customers')}
          active={orderByKey === 'customers'}
          order={sortDirection}
        >
          Customer
        </SortableHeader>
      ),
      rowRender: (rowData) => (
        <div>
          {rowData?.customers?.map((c, i) => (
            <div key={c.customer}>
              {`${c.customer} ${!(rowData.customers.length === i + 1) ? ', ' : ''}`}{' '}
            </div>
          ))}
        </div>
      ),
    },
    {
      width: '30%',
      key: 'projects',
      title: 'Projects',
      isSortable: false,
      headerRender: () => (
        <SortableHeader
          onClick={() => handleHeaderClick('projects')}
          active={orderByKey === 'projects'}
          order={sortDirection}
        >
          Projects
        </SortableHeader>
      ),
      rowRender: (rowData) => (
        <div>
          {rowData?.customers?.map((c, i) =>
            c?.projects?.map((p, pi) => (
              <div key={p}>
                {`${p} ${
                  !(rowData.customers.length === i + 1)
                    ? ', '
                    : c?.projects?.length !== pi + 1
                    ? ', '
                    : ''
                }`}{' '}
              </div>
            )),
          )}
        </div>
      ),
    },
    {
      width: '19%',
      key: 'userAddress',
      title: 'Address',
      isSortable: false,
      headerRender: () => (
        <SortableHeader
          onClick={() => handleHeaderClick('userAddress')}
          active={orderByKey === 'userAddress'}
          order={sortDirection}
        >
          Address
        </SortableHeader>
      ),
      rowRender: (rowData) => (
        <div>
          {rowData?.userAddress?.map((address, i) => (
            <div key={i}>
              {`${address?.addressName}${rowData?.userAddress?.length !== i + 1 ? ', ' : ''}`}
            </div>
          ))}
        </div>
      ),
    },
    {
      width: '10%',
      key: 'phoneNumber',
      title: 'Phone',
      isSortable: false,
      rowRender: (rowData) => <span className=''> {rowData?.phoneNumber} </span>,
    },
    {
      width: '19%',
      key: 'loginEmail',
      title: 'User ID',
      isSortable: false,
      headerRender: () => (
        <SortableHeader
          onClick={() => handleHeaderClick('loginEmail')}
          active={orderByKey === 'loginEmail'}
          order={sortDirection}
        >
          User ID
        </SortableHeader>
      ),
      rowRender: (rowData) => <span className='break-all'> {rowData?.loginEmail} </span>,
    },
    {
      width: '10%',
      key: 'actions',
      title: 'Actions',
      columClassName: 'flex justify-end',
      rowRender: (rowData) =>
        !isUserHasEditAndDeletePermission(rowData?.userRole) ? null : (
          <div>
            {/* Mobile */}
            <div className='flex items-center justify-end gap-6 break:hidden'>
              <div onClick={() => onClickEdit(rowData?.userId)}>
                <MobileEdit />
              </div>
              <div onClick={() => onClickDelete(rowData?.userId)}>
                <MobileDelete />
              </div>
            </div>

            {/* Desktop */}
            <div className='hidden gap-4 break:flex'>
              <NButton
                className='action-edit-btn'
                variant='outlined'
                startIcon={<EditIcon />}
                onClick={() => onClickEdit(rowData?.userId)}
              >
                Edit
              </NButton>
              <NButton
                className='action-delete-btn'
                variant='outlined'
                startIcon={<DeleteIcon />}
                onClick={() => onClickDelete(rowData?.userId)}
              >
                Delete
              </NButton>
            </div>
          </div>
        ),
    },
  ];

  useEffect(() => {
    fetOptions();
  }, []);

  const fetOptions = async () => {
    try {
      const response = await getCustomers();
      setCustomerOptions(response);
    } catch {}
  };

  const onClickEdit = (id) => {
    navigate(`${RouteEnums.USER_EDIT}/${id}`);
  };

  const onClickDelete = (id) => {
    const response = window.confirm('Press ok to delete user?');
    if (response) {
      successToaster(response?.message || 'Deleted Successfully');
      handleDeleteUser(id);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await deleteUser(userId);
      if (!response) return;
      fetchUsers();
    } catch {}
  };

  const getCustomerOptions = () => {
    if (customerOptions?.length) {
      return customerOptions?.map((o) => ({
        label: `${o?.customerName} (${o?.sapid})`,
        value: o?.id?.toString(),
      }));
    }
    return [];
  };

  const optionValueCustomer = (cusVal) => {
    const selectedObj = customerOptions?.find((o) => o?.id?.toString() === cusVal?.toString());
    return selectedObj ? { label: `${selectedObj?.customerName} (${selectedObj?.id})`, value: selectedObj?.id?.toString() } : undefined;
  };

  // project options depend on selected customer
  const getProjectOptions = () => {
    if (!customerId) return [];
    const selectedCustomer = customerOptions?.find(
      (o) => o?.id?.toString() === customerId?.toString(),
    );
    const projects = selectedCustomer?.projects || [];
    return projects.map((p) => ({ label: `${p.projectName} (${p.sapid})` , value: p.projectId?.toString() }));
  };

  // map selected projectIds to option objects for the dropdown value (multi-select)
  const optionValuesProject = (projIds) => {
    if (!Array.isArray(projIds) || !projIds.length) return [];
    const options = getProjectOptions();
    const idSet = new Set(projIds.map((id) => id?.toString()));
    return options.filter((opt) => idSet.has(opt.value?.toString()));
  };

  const handleDownloadUserList = async () => {
    try {
      const r = await downloadUserList(customerId);

      const url = window.URL.createObjectURL(new Blob([r]));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `UserList.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {}
  };

  return (
    <section className='n-table-wrapper pt-2'>
      {/* Mobile Header */}
      <div className='break:hidden'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='font-semibold'>Users</h2>
          <div>
            <div className='flex items-center justify-end gap-2'>
              <NButton variant='Noutline' onClick={() => navigate(RouteEnums.USER_ADD)}>
                Add User
              </NButton>
              <NButton
                isLoading={isLoading}
                className='!bg-nblue text-nblue'
                onClick={handleDownloadUserList}
              >
                <DownloadIcon />
              </NButton>
            </div>
          </div>
        </div>
        <div className='mb-4'>
          <NSearch
            value={searchString}
            onChange={(e) => {
              setSearchString(e?.target?.value);
            }}
            placeholder={"Search by Mobile, User Name, Project, Email ID"}
          />
        </div>
      </div>

      {/* Desktop */}
      <div className='mb-[30px] hidden flex-col items-start justify-between gap-5 break:flex xxl:flex-col xxl:items-center xxl:gap-10'>
        <div className='flex w-full flex-row flex-wrap gap-4'>
          <div className='w-full lg:w-[150px] flex-1 min-w-[260px]'>
            <NSearch
              value={searchString}
              onChange={(e) => {
                setSearchString(e?.target?.value);
              }}
              className={'!min-w-[150px]'}
              placeholder={'Search by Mobile, User Name, Project, Email ID'}
            />
          </div>
          <div className='w-full xl:w-4/12 xxl:w-4/12 min-w-[260px]'>
            <NSearchableDropDown
              label='Customer Name'
              name='customer'
              value={optionValueCustomer(customerId)}
              onChange={(e, selectedValue) => {
                setUsers(undefined);
                setPage(1);
                setCustomerId(selectedValue?.value);
                setProjectIds([]);
              }}
              className='w-full !bg-white'
              type='text'
              options={[{ label: 'Select Customer', value: '' }, ...getCustomerOptions()]}
            />
          </div>
          <div className='w-full xl:w-4/12 xxl:w-4/12 min-w-[260px]'>
            <NSearchableDropDown
              label='Project'
              name='project'
              multiple
              disableCloseOnSelect
              renderTags={(selected) => {
                const count = Array.isArray(selected) ? selected.length : 0;
                if (count === 0) return null;
                const full = (selected[0]?.label ?? '').toString();
                const maxLen = 20;
                const truncated = full.length > maxLen ? `${full.slice(0, maxLen)}…` : full;
                const extra = count - 1;
                return (
                  <span
                    className='ml-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600'
                    title={full}
                  >
                    {truncated}
                    {extra > 0 ? ` (+${extra})` : ''}
                  </span>
                );
              }}
              sx={{
                '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                '& .MuiAutocomplete-tag': { display: 'none' },
              }}
              value={optionValuesProject(projectIds)}
              onChange={(e, selectedValues) => {
                setUsers(undefined);
                setPage(1);
                const ids = (selectedValues || []).map((sv) => sv?.value);
                setProjectIds(ids);
              }}
              className='w-full !bg-white'
              type='text'
              options={[
                { label: 'Select Project', value: '' },
                ...getProjectOptions(),
              ]}
              isOptionEqualToValue={(o, v) => o?.value?.toString() === v?.value?.toString()}
              disabled={!customerId}
              limitTags={1}
            />
          </div>
        </div>
        <div className='flex w-full items-center justify-start gap-2 xl:w-auto xl:ml-auto xl:justify-end'>
          <NButton
            className='flex-shrink-0 flex-nowrap !bg-nblue text-nblue'
            onClick={() => navigate(RouteEnums.USER_ADD)}
          >
            <span className='flex flex-shrink-0'>Add User</span>
          </NButton>
          <NButton
            isLoading={isLoading}
            className='!bg-nblue !px-2 text-nblue'
            onClick={handleDownloadUserList}
            title='Download user list'
          >
            <DownloadIcon />
          </NButton>
        </div>
      </div>

      <div className={`${users?.length ? 'ntable-container' : ''}`}>
        <NTable2
          isLoading={isPaginationLoading}
          rows={users || []}
          columns={columns}
          lastElementRef={lastElementRef}
        />{' '}
      </div>
    </section>
  );
}

ManageUsers.propTypes = {};

export default ManageUsers;
